import dompurify from 'dompurify';
import { ChevronLeft, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetCourseForPull } from '@/hooks/queries/useGetCourseForPull';
import {
	PUBLISHED_COURSES_PAGE_SIZE,
	usePublishedCourses,
} from '@/hooks/queries/usePublishedCourses';
import { plainTextKeyForCompare, removeCommentTags, skillCompositeKey } from '@/lib/utils';
import type { PulledFocusImport } from '@/Pages/Courses/CourseEditContext';
import { sanitizeOptions } from '@/utils';

const EMPTY_SKILL_KEYS = new Set<string>();

type CoursePullPanelOutcomes = {
	mode: 'outcomes';
	currentCourseId: string;
	existingTexts: string[];
	onAdd: (texts: string[]) => void;
	onClose: () => void;
	label: string;
};

type CoursePullPanelFocuses = {
	mode: 'focuses';
	currentCourseId: string;
	existingTexts: string[];
	existingSkillKeys: Set<string>;
	skillTypeLabels: Record<string, string>;
	onAdd: (items: PulledFocusImport[]) => void;
	onClose: () => void;
	label: string;
};

export type CoursePullPanelProps = CoursePullPanelOutcomes | CoursePullPanelFocuses;

/** Rich text from TipTap / DB — same sanitizer pattern as OutcomeTab. */
function sanitizePullHtml(raw: string) {
	const stripped = removeCommentTags(raw);
	return dompurify.sanitize(stripped, { ...sanitizeOptions });
}

type PullFocusRow = {
	courses_focuses_id: string;
	title: string;
	skills: Array<{
		focuses_skills_id: string;
		skill_type: string;
		skill_text: string;
	}>;
};

export function CoursePullPanel(props: CoursePullPanelProps) {
	const { currentCourseId, onClose, label, mode } = props;
	const existingSkillKeysSet =
		props.mode === 'focuses' ? props.existingSkillKeys : EMPTY_SKILL_KEYS;

	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [page, setPage] = useState(0);
	const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
	const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
	const [checkedFocusTitle, setCheckedFocusTitle] = useState<Record<number, boolean>>({});
	const [checkedSkill, setCheckedSkill] = useState<Record<string, boolean>>({});

	useEffect(() => {
		const id = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
		return () => window.clearTimeout(id);
	}, [searchTerm]);

	const {
		data: listData,
		isLoading: listLoading,
		isFetching: listFetching,
	} = usePublishedCourses({
		searchTerm: debouncedSearch,
		page,
		currentCourseId,
		enabled: !selectedCourseId,
	});

	const {
		data: pullData,
		isLoading: pullLoading,
		isFetching: pullFetching,
	} = useGetCourseForPull(selectedCourseId);

	const existingSet = useMemo(() => {
		const set = new Set<string>();
		for (const t of props.existingTexts) {
			const key = plainTextKeyForCompare(t);
			if (key.length > 0) set.add(key);
		}
		return set;
	}, [props.existingTexts]);

	const isAlreadyAdded = useCallback(
		(text: string) => {
			const key = plainTextKeyForCompare(text);
			if (key.length === 0) return false;
			return existingSet.has(key);
		},
		[existingSet]
	);

	const outcomeItems = useMemo(() => {
		if (mode !== 'outcomes' || !pullData) return [];
		return (pullData.courses_outcomes ?? [])
			.map((o) => o.outcome?.outcome_text?.trim() ?? '')
			.filter(Boolean);
	}, [pullData, mode]);

	const focusRows = useMemo((): PullFocusRow[] => {
		if (mode !== 'focuses' || !pullData) return [];
		return (pullData.courses_focuses ?? [])
			.map((cf) => {
				const title = cf.focus?.focus_title?.trim() ?? '';
				const skills = (cf.focus?.focuses_skills ?? [])
					.map((fs) => ({
						focuses_skills_id: fs.focuses_skills_id,
						skill_type: fs.skill?.skill_type ?? '',
						skill_text: (fs.skill?.skill_text ?? '').trim(),
					}))
					.filter((s) => s.skill_text.length > 0);
				return {
					courses_focuses_id: cf.courses_focuses_id,
					title,
					skills,
				};
			})
			.filter((row) => row.title.length > 0 || row.skills.length > 0);
	}, [pullData, mode]);

	useEffect(() => {
		if (!selectedCourseId) return;
		if (mode === 'outcomes') {
			if (outcomeItems.length === 0) {
				setCheckedItems({});
				return;
			}
			const next: Record<number, boolean> = {};
			outcomeItems.forEach((text, i) => {
				next[i] = !isAlreadyAdded(text);
			});
			setCheckedItems(next);
			return;
		}
		if (focusRows.length === 0) {
			setCheckedFocusTitle({});
			setCheckedSkill({});
			return;
		}
		const nextT: Record<number, boolean> = {};
		const nextS: Record<string, boolean> = {};
		focusRows.forEach((row, fi) => {
			nextT[fi] = row.title.length > 0 && !isAlreadyAdded(row.title);
			for (const sk of row.skills) {
				nextS[sk.focuses_skills_id] = !existingSkillKeysSet.has(
					skillCompositeKey(sk.skill_type, sk.skill_text)
				);
			}
		});
		setCheckedFocusTitle(nextT);
		setCheckedSkill(nextS);
	}, [selectedCourseId, mode, outcomeItems, focusRows, isAlreadyAdded, existingSkillKeysSet]);

	const totalCount = listData?.totalCount ?? 0;
	const totalPages = totalCount > 0 ? Math.ceil(totalCount / PUBLISHED_COURSES_PAGE_SIZE) : 0;
	const listBusy = listLoading || listFetching;
	const pullBusy = pullLoading || pullFetching;

	const handleAddSelected = () => {
		if (mode === 'outcomes') {
			const selected = outcomeItems.filter((_, i) => checkedItems[i]);
			props.onAdd(selected);
		} else {
			const out: PulledFocusImport[] = [];
			focusRows.forEach((row, fi) => {
				const titleOn = checkedFocusTitle[fi];
				const chosenSkills = row.skills.filter((sk) => checkedSkill[sk.focuses_skills_id]);
				if (!titleOn && chosenSkills.length === 0) return;
				out.push({
					title: titleOn ? row.title : '',
					skills: chosenSkills.map((s) => ({
						skill_type: s.skill_type,
						skill_text: s.skill_text,
					})),
				});
			});
			props.onAdd(out);
		}
		onClose();
	};

	const goBackToCourseList = () => {
		setSelectedCourseId(null);
	};

	const step2Count = mode === 'outcomes' ? outcomeItems.length : focusRows.length;

	if (selectedCourseId) {
		return (
			<div className="rounded-lg border border-border bg-secondary/40 p-4 mb-5">
				<div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
					<div className="flex items-center gap-2 min-w-0">
						<button
							type="button"
							onClick={goBackToCourseList}
							className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
							aria-label="Back to course search"
						>
							<ChevronLeft size={18} />
						</button>
						<div className="flex flex-col min-w-0">
							<span className="text-[13.5px] font-semibold text-primary truncate">
								{pullData?.course_name ?? '…'}
							</span>
							<span className="text-[12px] font-normal text-muted-foreground">
								{mode === 'outcomes'
									? `Select outcomes to add (${step2Count})`
									: `Select ${label.toLowerCase()} to add (${step2Count})`}
							</span>
						</div>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<Button type="button" size="xs" onClick={handleAddSelected}>
							Add selected
						</Button>
						<button
							type="button"
							onClick={onClose}
							className="text-muted-foreground hover:text-foreground transition-colors p-1"
							aria-label="Close"
						>
							<X size={14} />
						</button>
					</div>
				</div>

				{pullBusy && (
					<div className="flex items-center gap-2 text-[13px] text-muted-foreground py-6 justify-center">
						<Loader2 size={16} className="animate-spin" />
						Loading…
					</div>
				)}

				{!pullBusy && mode === 'outcomes' && outcomeItems.length === 0 && (
					<p className="text-[13px] text-muted-foreground py-4">
						This course has no outcomes to copy.
					</p>
				)}

				{!pullBusy && mode === 'focuses' && focusRows.length === 0 && (
					<p className="text-[13px] text-muted-foreground py-4">
						This course has no {label.toLowerCase()} to copy.
					</p>
				)}

				{!pullBusy && mode === 'outcomes' && outcomeItems.length > 0 && (
					<div className="flex flex-col gap-1.5 max-h-[min(360px,50vh)] overflow-y-auto">
						{outcomeItems.map((text, i) => {
							const dup = isAlreadyAdded(text);
							return (
								<label
									key={`${i}-${text.slice(0, 40)}`}
									htmlFor={`pull-item-${i}`}
									className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left text-[13px] transition-colors cursor-pointer ${
										checkedItems[i]
											? 'border-border bg-white text-foreground'
											: 'border-transparent bg-white/50 text-muted-foreground'
									}`}
								>
									<Checkbox
										id={`pull-item-${i}`}
										checked={!!checkedItems[i]}
										disabled={dup}
										className="mt-0.5 shrink-0"
										onCheckedChange={() => setCheckedItems((prev) => ({ ...prev, [i]: !prev[i] }))}
									/>
									<span className="min-w-0 wrap-break-words [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-5">
										<span
											className="inline-block mr-2"
											// biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify + shared sanitizeOptions
											dangerouslySetInnerHTML={{ __html: sanitizePullHtml(text) }}
										/>
										{dup && (
											<span className="text-xs bg-yellow-100 text-primary inline-block rounded-md px-1.5 py-0.5">
												Already added
											</span>
										)}
									</span>
								</label>
							);
						})}
					</div>
				)}

				{!pullBusy && mode === 'focuses' && focusRows.length > 0 && (
					<div className="flex flex-col gap-3 max-h-[min(420px,55vh)] overflow-y-auto">
						{focusRows.map((row, fi) => {
							const titleDup = row.title.length > 0 && isAlreadyAdded(row.title);
							const skillTypeLabels =
								props.mode === 'focuses' ? props.skillTypeLabels : ({} as Record<string, string>);
							return (
								<div
									key={`focus-${row.courses_focuses_id}`}
									className="rounded-lg border border-border/80 bg-white/60 px-3 py-2.5"
								>
									<div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
										<span className="text-[12px] font-semibold text-primary">
											{label} {fi + 1}
										</span>
										<Button
											type="button"
											variant="ghost"
											size="xs"
											className="h-7 text-[11px]"
											onClick={() => {
												setCheckedFocusTitle((prev) => ({ ...prev, [fi]: true }));
												setCheckedSkill((prev) => {
													const next = { ...prev };
													for (const sk of row.skills) {
														next[sk.focuses_skills_id] = true;
													}
													return next;
												});
											}}
										>
											Select all
										</Button>
									</div>
									{row.title.length > 0 && (
										<label
											htmlFor={`pull-focus-title-${fi}`}
											className={`flex items-start gap-2.5 py-1.5 text-left text-[13px] cursor-pointer rounded-md ${
												checkedFocusTitle[fi] ? 'text-foreground' : 'text-muted-foreground'
											}`}
										>
											<Checkbox
												id={`pull-focus-title-${fi}`}
												checked={!!checkedFocusTitle[fi]}
												disabled={titleDup}
												className="mt-0.5 shrink-0"
												onCheckedChange={() =>
													setCheckedFocusTitle((prev) => ({
														...prev,
														[fi]: !prev[fi],
													}))
												}
											/>
											<span className="min-w-0 flex-1 wrap-break-words [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-5">
												<span className="text-[11px] font-medium text-muted-foreground block mb-0.5">
													Title
												</span>
												<span
													className="inline-block mr-2"
													// biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify + shared sanitizeOptions
													dangerouslySetInnerHTML={{ __html: sanitizePullHtml(row.title) }}
												/>
												{titleDup && (
													<span className="text-xs bg-yellow-100 text-primary inline-block rounded-md px-1.5 py-0.5 ml-1">
														Already added
													</span>
												)}
											</span>
										</label>
									)}
									{row.skills.length > 0 && (
										<ul className="mt-1 space-y-1 pl-1 border-l-2 border-border/60 ml-2">
											{row.skills.map((sk) => {
												const skDup = existingSkillKeysSet.has(
													skillCompositeKey(sk.skill_type, sk.skill_text)
												);
												const typeLabel =
													skillTypeLabels[sk.skill_type] ?? skillTypeLabels.skill ?? sk.skill_type;
												return (
													<li key={`sk-${sk.focuses_skills_id}`}>
														<label
															htmlFor={`pull-skill-${sk.focuses_skills_id}`}
															className={`flex items-start gap-2 py-1 text-[12px] cursor-pointer rounded ${
																checkedSkill[sk.focuses_skills_id]
																	? 'text-foreground'
																	: 'text-muted-foreground'
															}`}
														>
															<Checkbox
																id={`pull-skill-${sk.focuses_skills_id}`}
																checked={!!checkedSkill[sk.focuses_skills_id]}
																disabled={skDup}
																className="mt-0.5 shrink-0"
																onCheckedChange={() =>
																	setCheckedSkill((prev) => ({
																		...prev,
																		[sk.focuses_skills_id]: !prev[sk.focuses_skills_id],
																	}))
																}
															/>
															<span className="min-w-0 flex-1 wrap-break-words">
																<span className="text-[10px] uppercase tracking-wide text-muted-foreground block">
																	{typeLabel}
																</span>
																<span
																	className="[&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-0.5 [&_ul]:list-disc [&_ul]:pl-5"
																	// biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify + shared sanitizeOptions
																	dangerouslySetInnerHTML={{
																		__html: sanitizePullHtml(sk.skill_text),
																	}}
																/>
																{skDup && (
																	<span className="text-[10px] bg-yellow-100 text-primary inline-block rounded px-1 py-0.5 mt-0.5">
																		Already added
																	</span>
																)}
															</span>
														</label>
													</li>
												);
											})}
										</ul>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-border bg-secondary/40 p-4 mb-5">
			<div className="flex items-center justify-between mb-3">
				<div className="text-[13.5px] font-semibold text-primary">Copy from another course</div>
				<button
					type="button"
					onClick={onClose}
					className="text-muted-foreground hover:text-foreground transition-colors p-1"
					aria-label="Close"
				>
					<X size={14} />
				</button>
			</div>

			<p className="text-[12px] text-muted-foreground mb-3">
				{mode === 'outcomes' ? (
					<>
						Choose another course at your school, then pick which <strong>outcomes</strong> to copy
						into this course. Only published curriculum is used—not draft or working copy.
					</>
				) : (
					<>
						Choose another course at your school, then pick which{' '}
						<strong>{label.toLowerCase()}</strong> (titles and sub-items) to copy into this course.
						Only published curriculum is used—not draft or working copy.
					</>
				)}
			</p>

			<Input
				type="search"
				placeholder="Search courses…"
				value={searchTerm}
				onChange={(e) => {
					setPage(0);
					setSearchTerm(e.target.value);
				}}
				className="mb-3"
				autoComplete="off"
			/>

			<div className="max-h-[min(280px,40vh)] overflow-y-auto mb-3 rounded-md border border-border/60 bg-background/50">
				{listBusy && (
					<div className="p-3 space-y-2">
						{(['sk1', 'sk2', 'sk3', 'sk4', 'sk5'] as const).map((sk) => (
							<Skeleton key={sk} className="h-9 w-full" />
						))}
					</div>
				)}

				{!listBusy && totalCount === 0 && (
					<p className="text-[13px] text-muted-foreground p-4">No courses match your search.</p>
				)}

				{!listBusy && totalCount > 0 && (
					<ul className="divide-y divide-border/60">
						{(listData?.courses ?? []).map((c) => (
							<li key={c.course_id}>
								<button
									type="button"
									onClick={() => setSelectedCourseId(c.course_id)}
									className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-secondary/80 transition-colors"
								>
									{c.course_name}
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			{totalPages > 0 && (
				<div className="flex items-center justify-between gap-2 flex-wrap text-[12px] text-muted-foreground">
					<Button
						type="button"
						variant="secondary"
						size="xs"
						disabled={page <= 0 || listBusy}
						onClick={() => setPage((p) => Math.max(0, p - 1))}
					>
						Previous
					</Button>
					<span>
						Page {page + 1} of {totalPages}
					</span>
					<Button
						type="button"
						variant="secondary"
						size="xs"
						disabled={page >= totalPages - 1 || listBusy}
						onClick={() => setPage((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
}
